import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { Ritmo } from '#components/mobile/avisos/avisos';
import {
  pedirPermisoAvisos,
  useAvisos,
} from '#components/mobile/avisos/useAvisos';
import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';

import {
  estiloOpcion,
  estiloOpciones,
  HojaTravesia,
  Marca,
  TituloHoja,
} from './comun';

type Opcion = Ritmo | 'no';
const OPCIONES: readonly Opcion[] = ['dia', 'dos', 'varias', 'manera', 'no'];
const EMOJI: Record<Opcion, string> = {
  dia: '☀️',
  dos: '🌊',
  varias: '☕',
  manera: '🧭',
  no: '🤫',
};

type HojaNotificacionesProps = {
  ocupado?: boolean;
  onContinuar: () => void;
};

/**
 * Paso 8 · «¿Cada cuánto quieres que pase a recordarte tus cuentas?». Guarda
 * el ritmo en las notificaciones de este dispositivo (pref global
 * `avisosGavi`) y, si no es «Mejor no», pide el permiso del sistema justo
 * tras el toque. Lo demás (horas, días, tono) se cambia en Ajustes.
 */
export function HojaNotificaciones({
  ocupado,
  onContinuar,
}: HojaNotificacionesProps) {
  const { t } = useTranslation();
  const { avisos, setAvisos } = useAvisos();
  const [elegida, setElegida] = useState<Opcion>(
    avisos.activo ? avisos.ritmo : 'dia',
  );

  const titulo: Record<Opcion, string> = {
    dia: t('Every day'),
    dos: t('Every 2 days'),
    varias: t('Several times a day'),
    manera: t('My way'),
    no: t('Better not, thanks'),
  };
  const detalle: Record<Opcion, string> = {
    dia: t('A nudge at {{hora}}', { hora: avisos.hora }),
    dos: t('To take it easy'),
    varias: t('To jot down every coffee'),
    manera: t('You set the times and days'),
    no: t('You can change it in Settings'),
  };

  async function continuar() {
    if (elegida === 'no') {
      setAvisos({ ...avisos, activo: false });
    } else {
      // El permiso se pide con el toque aún «caliente» (Android lo exige).
      const ok = await pedirPermisoAvisos();
      setAvisos({ ...avisos, ritmo: elegida, activo: ok });
    }
    onContinuar();
  }

  return (
    <HojaTravesia
      aria-label={t('Notifications')}
      data-testid="hoja-notificaciones"
      cabeza={
        <Bocadillo
          variante="hoja"
          estado="ladea"
          texto={t(
            'Graaac! How often do you want me to come by and remind you about your accounts? You are at the helm.',
          )}
        >
          <span
            style={{
              ...texto.secundario,
              display: 'block',
              marginTop: 6,
              fontWeight: 500,
              color: color.fg2,
            }}
          >
            <Trans>
              If you are not sure, pick one on the fly: you can change it
              whenever you want in Settings, under "Notifications".
            </Trans>
          </span>
        </Bocadillo>
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado}
          data-testid="travesia-continuar"
          onPress={() => void continuar()}
        >
          <Trans>Continue</Trans>
        </Boton>
      }
    >
      <TituloHoja>
        <Trans>Notifications</Trans>
      </TituloHoja>
      <div style={estiloOpciones}>
        {OPCIONES.map((o, i) => (
          <button
            type="button"
            key={o}
            aria-pressed={elegida === o}
            data-testid={`notif-opcion-${o}`}
            style={estiloOpcion(i === 0)}
            onClick={() => setElegida(o)}
          >
            <span
              aria-hidden
              style={{ fontSize: 20, width: 28, textAlign: 'center' }}
            >
              {EMOJI[o]}
            </span>
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
              {titulo[o]}
              <span
                style={{
                  ...texto.secundario,
                  display: 'block',
                  fontWeight: 500,
                  color: color.fg2,
                }}
              >
                {detalle[o]}
              </span>
            </span>
            <Marca marcada={elegida === o} />
          </button>
        ))}
      </div>
    </HojaTravesia>
  );
}
